import * as React from "react";
import { Text } from "@uikit";
import XE from "@components/common/xe";
import {
  calcCrow,
  getCheapestPackage,
  getPackageTotalCostForDate,
  slugify,
  Region,
  roundTo,
} from "@kalabo/golfsavers-common";

import { isRegionalSite } from "@utilities/core";
import { useBreakpoint } from "use-breakpoint";
import { BREAKPOINTS } from "@utilities/constants";
import { Hotel, Hotel_Package } from "@kalabo/golfsavers-common";
import { Link } from "gatsby";
import StarRating from "@components/starRating";
import { FaMapMarkerAlt } from "@react-icons/all-files/fa/FaMapMarkerAlt";

import { Swiper, SwiperSlide } from "swiper/react";
// import required modules
import { Navigation, Pagination } from "swiper";
// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import SliderNavigation from "@components/common/sliderNavigation";
import { useApp } from "@store/app";
import { ChevronRightIcon } from "@heroicons/react/outline";
import _ from "lodash";

interface IProps {
  course: any;
  region: Region;
}

const CoursePackages = ({ region, course }: IProps) => {
  const { breakpoint } = useBreakpoint(BREAKPOINTS, "mobile");

  const search = useApp(($) => $.search);
  const setHotelFilters = useApp(($) => $.setHotelFilters);
  const display_currency = useApp(($) => $.display_currency);
  const package_date = useApp(($) => $.package_date);

  let hotels = course?.hotels
    .map((a: any) => {
      let images = a.images;
      return {
        ...a,
        featured_image: images?.length > 0 ? images[0].url : null,
      };
    })
    .sort((a: Hotel, b: Hotel) => {
      return (
        calcCrow(course.latitude, course.longitude, a.latitude, a.longitude)
          .km -
        calcCrow(course.latitude, course.longitude, b.latitude, b.longitude).km
      );
    }) as Hotel[];

  const renderSmall = React.useCallback(
    (pkg: Hotel_Package, hotel: any, index: any) => {
      return (
        <Link
          to={`/${slugify(
            process.env.GATSBY_REGION.toLowerCase()
          )}-golf-packages/${hotel.slug}`}
          className={`group bg-white rounded-[6px] mt-4 mb-6 relative flex flex-col col-span-6 sm:col-span-4 lg:col-span-3 shadow-card overflow-hidden`}
        >
          <div
            className={`aspect-[5/3] rounded-[4px] overflow-hidden bg-black `}
          >
            <img
              loading="lazy"
              width="100%"
              height="160"
              className="group-hover:opacity-90 transition-all"
              alt={`${hotel.name.en} golf package`}
              src={`${process.env.GATSBY_IMAGE_PREFIX}${hotel.featured_image}?aspect_ratio=5:3&width=300`}
            />
          </div>
          <div className=" flex flex-col gap-1  px-3 py-4">
            <Text
              size={17}
              weight="semibold"
              color="black90"
              leading={1.3}
              element="h3"
              className="group-hover:text-blue group-hover:underline text-ellipsis overflow-hidden w-full whitespace-nowrap"
            >
              {hotel.name.en}
            </Text>

            <div className="flex items-center gap-2 py-1">
              <StarRating
                hideCount
                hideEmpty
                color="green"
                rating={hotel.star_rating}
                size="small"
              />
              <div className="sm:block bg-grey3 h-[18px] w-[1px] border-grey2"></div>
              <Text
                sizeTablet={13}
                sizeDesktop={13}
                size={13}
                color="black80"
                leading={1.3}
                className="block  overflow-hidden"
              >
                <div className="flex gap-1 items-center">
                  <FaMapMarkerAlt className="w-4 h-4 text-grey6" />
                  <span className=" text-ellipsis overflow-hidden w-full whitespace-nowrap">
                    {hotel.area?.name?.en}
                  </span>
                </div>
              </Text>
            </div>
          </div>

          <div className="flex justify-between  border-t border-grey3 mx-3 py-4">
            <div className="flex flex-1 flex-col gap-[6px] overflow-hidden">
              <Text size={15} weight="semibold" color="black90" leading={1}>
                {breakpoint === "ultrawide" ? (
                  <>
                    {pkg.number_of_nights}N/
                    {pkg?.courses?.length}R
                  </>
                ) : (
                  <>
                    {pkg.number_of_nights}N / {pkg?.courses?.length} round
                    {pkg?.courses?.length > 1 ? "s" : ""}
                  </>
                )}
              </Text>

              <Text
                size={11}
                color="black60"
                leading={1.3}
                className="text-ellipsis overflow-hidden w-full- whitespace-nowrap"
              >
                {pkg.course_combo}
              </Text>
            </div>
            <div className="flex flex-1 flex-col  gap-[6px] items-end">
              <div className="flex items-end gap-1">
                <Text size={9} color="black60" leading={1}>
                  from
                </Text>
                <Text size={15} color={"black90"} weight="semibold" leading={1}>
                  <XE
                    simplify
                    hide_decimal
                    base_currency={pkg.currency_code}
                    currency={display_currency.code}
                    value={getPackageTotalCostForDate(package_date, pkg)}
                  />
                </Text>
              </div>

              <Text size={11} color="black60" leading={1}>
                per person
              </Text>
            </div>
          </div>
        </Link>
      );
    },
    [breakpoint, display_currency]
  );

  return (
    <div className="relative">
      <div className="flex justify-between items-center px-[20px] md:px-[40px] lg:px-0 mb-2">
        <Text
          size={21}
          element="h2"
          weight="semibold"
          color="black"
          leading={1.3}
        >
          Golf & Hotel Packages Including {course.name.en}
        </Text>
        <Link
          onClick={() => {
            let filters = _.cloneDeep(search.hotel.filters);
            filters.rating = [];
            filters.hotel_types = [];
            filters.courses = [
              {
                name: course.name.en,
                value: course.course_id,
              },
            ];

            setHotelFilters(filters);
          }}
          to={
            isRegionalSite()
              ? `/${region.slug}-golf-packages?scroll=true`
              : `/${slugify(region.country.slug)}-golf-packages/${slugify(
                  region.name.en.toLowerCase()
                )}-golf-holidays?scroll=true`
          }
          className="hidden sm:block"
        >
          <Text
            size={15}
            weight="semibold"
            color="black"
            className="flex items-center hover:text-blue underline whitespace-nowrap"
          >
            See all{" "}
            <span>
              <ChevronRightIcon className="w-4" />
            </span>
          </Text>
        </Link>
      </div>
      <Swiper
        spaceBetween={14}
        breakpoints={{
          // when window width is >= 320px
          320: {
            slidesOffsetBefore: 20,
            slidesOffsetAfter: 20,
            slidesPerGroup: 1,
            slidesPerView: 1.4,
          },
          // when window width is >= 640px
          640: {
            slidesOffsetBefore: 40,
            slidesOffsetAfter: 40,
            slidesPerView: 3,
            slidesPerGroup: 1,
          },
          // when window width is >= 768px
          1024: {
            slidesPerView: 4,
            slidesPerGroup: 4,
          },
        }}
        pagination={{
          el: "#swiper-courses-pagination",
          clickable: true,
        }}
        navigation={{
          nextEl: ".slide-arrow__next__reviews",
          prevEl: ".slide-arrow__prev__reviews",
          enabled: true,
        }}
        modules={[Navigation, Pagination]}
      >
        {hotels
          .map((hotel: Hotel, index: number) => {
            let pkg = getCheapestPackage(package_date, hotel.packages);
            return (
              <SwiperSlide key={index}>
                {renderSmall(pkg, hotel, index)}
              </SwiperSlide>
            );
          })
          .splice(0, 12)}
      </Swiper>
      <SliderNavigation
        prevArrowClassName="slide-arrow__prev__reviews"
        nextArrowClassName="slide-arrow__next__reviews"
      />
      <div
        id="swiper-courses-pagination"
        className="swiper-black items-center flex justify-center w-full "
      ></div>
      <Link
        to={
          isRegionalSite()
            ? `/${region.slug}-golf-packages?scroll=true`
            : `/${slugify(region.country.slug)}-golf-packages/${slugify(
                region.name.en.toLowerCase()
              )}-golf-holidays?scroll=true`
        }
        className="sm:hidden flex flex-1 justify-center mt-8"
      >
        <Text
          size={15}
          weight="semibold"
          color="black"
          className="underline flex items-center hover:text-blue"
        >
          See all{" "}
          <span>
            <ChevronRightIcon className="w-4" />
          </span>
        </Text>
      </Link>
    </div>
  );
};

export default CoursePackages;
